import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';

interface Dependent {
  id: string;
  name: string;
  relationship: string;
  birthDate: string;
  income: number;
  disability: string;
}

interface YearEndDocument {
  id: string;
  documentType: string;
  fileName: string;
  fileUrl: string;
  uploadedAt: string;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
}

interface YearEndAdjustmentData {
  id: string;
  year: number;
  status: 'not_started' | 'in_progress' | 'submitted' | 'approved' | 'completed';
  completedSteps: string[];
  submittedAt?: string;
  approvedAt?: string;
  
  // 基本情報
  hasSpouse: boolean;
  spouseIncome?: number;
  
  // 保険料
  lifeInsurance: number;
  earthquakeInsurance: number;
  socialInsurance: number;
  medicalExpense: number;
  
  // 住宅ローン
  hasHousingLoan: boolean;
  housingLoanBalance?: number;
  housingLoanDeduction?: number;
  
  // iDeCo
  idecoContribution: number;
  
  // 扶養家族
  dependents: Dependent[];
  
  // 書類
  documents: YearEndDocument[];
}

const documentTypes = [
  { value: 'life_insurance', label: '生命保険料控除証明書' },
  { value: 'earthquake_insurance', label: '地震保険料控除証明書' },
  { value: 'housing_loan', label: '住宅ローン控除証明書' },
  { value: 'medical_expense', label: '医療費控除証明書' },
  { value: 'ideco', label: 'iDeCo払込証明書' },
  { value: 'social_insurance', label: '国民健康保険・国民年金証明書' },
  { value: 'other', label: 'その他' },
];

const steps = [
  { id: 'basic', label: '基本情報', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
  { id: 'spouse', label: '配偶者情報', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
  { id: 'dependents', label: '扶養家族', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
  { id: 'insurance', label: '保険料控除', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
  { id: 'housing', label: '住宅ローン', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { id: 'documents', label: '証明書', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { id: 'confirm', label: '確認・提出', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
];

export default function YearEndAdjustment() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<YearEndAdjustmentData | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('');
  const [uploadFile_, setUploadFile_] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await api.get<YearEndAdjustmentData>(`/api/year-end-adjustment/${currentYear}`);
      setData(result);
    } catch (error) {
      console.error('Failed to fetch year-end adjustment data:', error);
      // 初期データを設定
      setData({
        id: '',
        year: currentYear,
        status: 'not_started',
        completedSteps: [],
        hasSpouse: false,
        lifeInsurance: 0,
        earthquakeInsurance: 0,
        socialInsurance: 0,
        medicalExpense: 0,
        hasHousingLoan: false,
        idecoContribution: 0,
        dependents: [],
        documents: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (stepId: string) => {
    if (!data || saving) return;
    setSaving(true);
    try {
      await api.put(`/api/year-end-adjustment/${currentYear}`, {
        ...data,
        completedSteps: [...new Set([...data.completedSteps, stepId])],
        status: data.status === 'not_started' ? 'in_progress' : data.status,
      });
      setData(prev => prev ? {
        ...prev,
        completedSteps: [...new Set([...prev.completedSteps, stepId])],
        status: prev.status === 'not_started' ? 'in_progress' : prev.status,
      } : null);
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = () => {
    if (!data) return;
    setShowSubmitConfirm(true);
  };

  const confirmSubmit = async () => {
    if (saving) return;
    setShowSubmitConfirm(false);
    setSaving(true);
    try {
      await api.put(`/api/year-end-adjustment/${currentYear}/submit`, {});
      setData(prev => prev ? { ...prev, status: 'submitted', submittedAt: new Date().toISOString() } : null);
      toast.success('年末調整を提出しました');
    } catch (error) {
      console.error('Failed to submit:', error);
      toast.error('提出に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile_ || !uploadDocType) return;

    setUploading(true);
    try {
      const fileUrl = await api.uploadFile(uploadFile_, { folder: 'year-end-documents' });
      await api.post(`/api/year-end-adjustment/${currentYear}/documents`, {
        documentType: uploadDocType,
        fileName: uploadFile_.name,
        fileUrl,
      });
      setShowUploadModal(false);
      setUploadDocType('');
      setUploadFile_(null);
      fetchData();
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast.error('アップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'info' | 'warning' | 'success'> = {
      not_started: 'default',
      in_progress: 'info',
      submitted: 'warning',
      approved: 'success',
      completed: 'success',
    };
    const labels: Record<string, string> = {
      not_started: '未開始',
      in_progress: '入力中',
      submitted: '提出済',
      approved: '承認済',
      completed: '完了',
    };
    return <Badge variant={variants[status] || 'default'}>{labels[status] || status}</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">データの読み込みに失敗しました</p>
      </div>
    );
  }

  const isSubmitted = data.status === 'submitted' || data.status === 'approved' || data.status === 'completed';

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">年末調整</h1>
          <p className="text-sm text-gray-500">{currentYear}年分</p>
        </div>
        {getStatusBadge(data.status)}
      </div>

      {/* 提出済みの場合 */}
      {isSubmitted && (
        <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
          <div className="flex items-center gap-3">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="font-semibold text-green-800 dark:text-green-200">年末調整が提出されました</p>
              <p className="text-sm text-green-600 dark:text-green-400">
                提出日: {data.submittedAt ? new Date(data.submittedAt).toLocaleDateString('ja-JP') : '-'}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* ステップインジケーター */}
      <div className="flex overflow-x-auto pb-2">
        {steps.map((step, index) => {
          const isCompleted = data.completedSteps.includes(step.id);
          const isCurrent = index === currentStep;
          return (
            <button
              key={step.id}
              onClick={() => setCurrentStep(index)}
              disabled={isSubmitted}
              className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                isCurrent
                  ? 'border-blue-600 text-blue-600'
                  : isCompleted
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={step.icon} />
              </svg>
              <span className="text-sm font-medium whitespace-nowrap">{step.label}</span>
              {isCompleted && (
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          );
        })}
      </div>

      {/* ステップコンテンツ */}
      <Card>
        {/* 基本情報 */}
        {currentStep === 0 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">基本情報</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-500">氏名</p>
                <p className="font-medium text-gray-900 dark:text-white">{user?.name}</p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-500">メールアドレス</p>
                <p className="font-medium text-gray-900 dark:text-white">{user?.email}</p>
              </div>
            </div>
            <p className="text-sm text-gray-500">基本情報はプロフィールから取得されます。変更が必要な場合はプロフィール画面から更新してください。</p>
          </div>
        )}

        {/* 配偶者情報 */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">配偶者情報</h2>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.hasSpouse}
                  onChange={(e) => setData(prev => prev ? { ...prev, hasSpouse: e.target.checked } : null)}
                  disabled={isSubmitted}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700 dark:text-gray-300">配偶者がいる</span>
              </label>
            </div>
            {data.hasSpouse && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="配偶者の年間所得（見込み）"
                  type="number"
                  value={data.spouseIncome || ''}
                  onChange={(e) => setData(prev => prev ? { ...prev, spouseIncome: parseInt(e.target.value) || 0 } : null)}
                  disabled={isSubmitted}
                  placeholder="円"
                />
              </div>
            )}
            <p className="text-sm text-gray-500">
              配偶者の年間所得が48万円以下の場合、配偶者控除の対象となります。
            </p>
          </div>
        )}

        {/* 扶養家族 */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">扶養家族</h2>
              {!isSubmitted && (
                <Button size="sm" onClick={() => {
                  setData(prev => prev ? {
                    ...prev,
                    dependents: [...prev.dependents, { id: Date.now().toString(), name: '', relationship: '', birthDate: '', income: 0, disability: '' }]
                  } : null);
                }}>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  追加
                </Button>
              )}
            </div>
            {data.dependents.length === 0 ? (
              <p className="text-center text-gray-500 py-8">扶養家族がいません</p>
            ) : (
              <div className="space-y-4">
                {data.dependents.map((dep, index) => (
                  <div key={dep.id} className="p-4 border dark:border-gray-700 rounded-lg">
                    <div className="flex justify-between items-start mb-4">
                      <span className="text-sm font-medium text-gray-500">扶養家族 {index + 1}</span>
                      {!isSubmitted && (
                        <Button variant="ghost" size="sm" onClick={() => {
                          setData(prev => prev ? {
                            ...prev,
                            dependents: prev.dependents.filter((_, i) => i !== index)
                          } : null);
                        }}>
                          削除
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="氏名"
                        value={dep.name}
                        onChange={(e) => {
                          const newDeps = [...data.dependents];
                          newDeps[index].name = e.target.value;
                          setData(prev => prev ? { ...prev, dependents: newDeps } : null);
                        }}
                        disabled={isSubmitted}
                        required
                      />
                      <Select
                        label="続柄"
                        value={dep.relationship}
                        onChange={(e) => {
                          const newDeps = [...data.dependents];
                          newDeps[index].relationship = e.target.value;
                          setData(prev => prev ? { ...prev, dependents: newDeps } : null);
                        }}
                        disabled={isSubmitted}
                      >
                        <option value="">選択してください</option>
                        <option value="child">子</option>
                        <option value="parent">親</option>
                        <option value="grandparent">祖父母</option>
                        <option value="sibling">兄弟姉妹</option>
                        <option value="other">その他</option>
                      </Select>
                      <Input
                        label="生年月日"
                        type="date"
                        value={dep.birthDate}
                        onChange={(e) => {
                          const newDeps = [...data.dependents];
                          newDeps[index].birthDate = e.target.value;
                          setData(prev => prev ? { ...prev, dependents: newDeps } : null);
                        }}
                        disabled={isSubmitted}
                      />
                      <Input
                        label="年間所得（見込み）"
                        type="number"
                        value={dep.income || ''}
                        onChange={(e) => {
                          const newDeps = [...data.dependents];
                          newDeps[index].income = parseInt(e.target.value) || 0;
                          setData(prev => prev ? { ...prev, dependents: newDeps } : null);
                        }}
                        disabled={isSubmitted}
                        placeholder="円"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 保険料控除 */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">保険料控除</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="生命保険料（年間支払額）"
                type="number"
                value={data.lifeInsurance || ''}
                onChange={(e) => setData(prev => prev ? { ...prev, lifeInsurance: parseInt(e.target.value) || 0 } : null)}
                disabled={isSubmitted}
                placeholder="円"
              />
              <Input
                label="地震保険料（年間支払額）"
                type="number"
                value={data.earthquakeInsurance || ''}
                onChange={(e) => setData(prev => prev ? { ...prev, earthquakeInsurance: parseInt(e.target.value) || 0 } : null)}
                disabled={isSubmitted}
                placeholder="円"
              />
              <Input
                label="社会保険料（国民健康保険・国民年金など）"
                type="number"
                value={data.socialInsurance || ''}
                onChange={(e) => setData(prev => prev ? { ...prev, socialInsurance: parseInt(e.target.value) || 0 } : null)}
                disabled={isSubmitted}
                placeholder="円"
              />
              <Input
                label="医療費（年間支払額）"
                type="number"
                value={data.medicalExpense || ''}
                onChange={(e) => setData(prev => prev ? { ...prev, medicalExpense: parseInt(e.target.value) || 0 } : null)}
                disabled={isSubmitted}
                placeholder="円"
              />
              <Input
                label="iDeCo掛金（年間支払額）"
                type="number"
                value={data.idecoContribution || ''}
                onChange={(e) => setData(prev => prev ? { ...prev, idecoContribution: parseInt(e.target.value) || 0 } : null)}
                disabled={isSubmitted}
                placeholder="円"
              />
            </div>
          </div>
        )}

        {/* 住宅ローン */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">住宅ローン控除</h2>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.hasHousingLoan}
                  onChange={(e) => setData(prev => prev ? { ...prev, hasHousingLoan: e.target.checked } : null)}
                  disabled={isSubmitted}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700 dark:text-gray-300">住宅ローン控除を受ける</span>
              </label>
            </div>
            {data.hasHousingLoan && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="住宅ローン年末残高"
                  type="number"
                  value={data.housingLoanBalance || ''}
                  onChange={(e) => setData(prev => prev ? { ...prev, housingLoanBalance: parseInt(e.target.value) || 0 } : null)}
                  disabled={isSubmitted}
                  placeholder="円"
                />
                <Input
                  label="住宅ローン控除額"
                  type="number"
                  value={data.housingLoanDeduction || ''}
                  onChange={(e) => setData(prev => prev ? { ...prev, housingLoanDeduction: parseInt(e.target.value) || 0 } : null)}
                  disabled={isSubmitted}
                  placeholder="円"
                />
              </div>
            )}
            <p className="text-sm text-gray-500">
              住宅ローン控除は2年目以降の方が対象です。1年目の方は確定申告が必要です。
            </p>
          </div>
        )}

        {/* 証明書アップロード */}
        {currentStep === 5 && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">控除証明書</h2>
              {!isSubmitted && (
                <Button size="sm" onClick={() => setShowUploadModal(true)}>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  アップロード
                </Button>
              )}
            </div>
            {data.documents.length === 0 ? (
              <p className="text-center text-gray-500 py-8">アップロードされた証明書はありません</p>
            ) : (
              <div className="space-y-2">
                {data.documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{doc.fileName}</p>
                        <p className="text-xs text-gray-500">
                          {documentTypes.find(t => t.value === doc.documentType)?.label || doc.documentType}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={doc.status === 'approved' ? 'success' : doc.status === 'rejected' ? 'danger' : 'warning'}>
                        {doc.status === 'approved' ? '承認' : doc.status === 'rejected' ? '却下' : '確認中'}
                      </Badge>
                      <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                        表示
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 確認・提出 */}
        {currentStep === 6 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">確認・提出</h2>
            
            {/* 入力内容サマリー */}
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h3 className="font-medium text-gray-700 dark:text-gray-300 mb-2">保険料控除</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-gray-500">生命保険料</span>
                  <span className="text-right">{formatCurrency(data.lifeInsurance)}</span>
                  <span className="text-gray-500">地震保険料</span>
                  <span className="text-right">{formatCurrency(data.earthquakeInsurance)}</span>
                  <span className="text-gray-500">iDeCo</span>
                  <span className="text-right">{formatCurrency(data.idecoContribution)}</span>
                </div>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h3 className="font-medium text-gray-700 dark:text-gray-300 mb-2">扶養家族</h3>
                <p className="text-sm">{data.dependents.length}名</p>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h3 className="font-medium text-gray-700 dark:text-gray-300 mb-2">証明書</h3>
                <p className="text-sm">{data.documents.length}件アップロード済み</p>
              </div>
            </div>

            {!isSubmitted && (
              <div className="pt-4 border-t dark:border-gray-700">
                <p className="text-sm text-gray-500 mb-4">
                  内容をご確認の上、提出ボタンを押してください。提出後は修正できません。
                </p>
                <Button onClick={handleSubmit} disabled={saving} className="w-full">
                  {saving ? '提出中...' : '年末調整を提出する'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ナビゲーション */}
        {!isSubmitted && (
          <div className="flex justify-between pt-6 mt-6 border-t dark:border-gray-700">
            <Button
              variant="secondary"
              onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
              disabled={currentStep === 0}
            >
              前へ
            </Button>
            <div className="flex gap-2">
              {currentStep < 6 && (
                <>
                  <Button variant="secondary" onClick={() => handleSave(steps[currentStep].id)} disabled={saving}>
                    {saving ? '保存中...' : '保存'}
                  </Button>
                  <Button onClick={() => {
                    handleSave(steps[currentStep].id);
                    setCurrentStep(prev => Math.min(6, prev + 1));
                  }}>
                    次へ
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* 提出確認モーダル */}
      <Modal isOpen={showSubmitConfirm} onClose={() => setShowSubmitConfirm(false)} title="年末調整の提出">
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">年末調整を提出しますか？提出後は修正できません。</p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowSubmitConfirm(false)}>キャンセル</Button>
            <Button onClick={confirmSubmit}>提出</Button>
          </div>
        </div>
      </Modal>

      {/* アップロードモーダル */}
      <Modal isOpen={showUploadModal} onClose={() => { setShowUploadModal(false); setUploadDocType(''); setUploadFile_(null); }} title="証明書をアップロード">
        <div className="space-y-4">
          <Select
            label="書類の種類"
            value={uploadDocType}
            onChange={(e) => setUploadDocType(e.target.value)}
          >
            <option value="">選択してください</option>
            {documentTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </Select>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">ファイル</label>
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => setUploadFile_(e.target.files?.[0] || null)}
              accept=".pdf,.jpg,.jpeg,.png"
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">PDF、JPG、PNG形式</p>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setShowUploadModal(false)}>キャンセル</Button>
            <Button onClick={handleUploadDocument} disabled={uploading || !uploadDocType || !uploadFile_}>
              {uploading ? 'アップロード中...' : 'アップロード'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
